import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Download, ArrowLeft } from "lucide-react";
import { api, clearReward } from "../lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await api.paymentStatus(sessionId);
        setInfo(r);
        if (r.payment_status === "paid") {
          setState("paid");
          clearReward(); // one-time reward consumed on a successful purchase
          return;
        }
        if (r.status === "expired" || r.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.debug("payment status poll retry", err?.message || err);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-slate-900 p-6">
      <div className="relative w-full max-w-md border border-white/10 bg-slate-950 p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-emerald-400" />
            <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-white">Confirming payment…</h1>
            <p className="mt-2 text-sm text-slate-400">Hang tight while we verify your purchase.</p>
          </>
        )}
        {state === "paid" && (
          <div data-testid="payment-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center bg-emerald-500 text-slate-900">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tighter text-white">Payment complete</h1>
            <p className="mt-2 text-sm text-slate-400">
              Thanks for buying <span className="text-emerald-400">{info?.title}</span>. Your download is ready.
            </p>
            <a href={api.downloadUrl(sessionId)} className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-emerald-500 px-6 py-3.5 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400">
              <Download className="h-4 w-4" /> Download template
            </a>
            <Link to="/library" data-testid="success-library-link" className="mt-3 inline-flex w-full items-center justify-center gap-2 border border-white/15 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:border-white/40">
              View my library
            </Link>
            <Link to="/" className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors duration-200 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Back to marketplace
            </Link>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-pending">
            <div className="mx-auto flex h-14 w-14 items-center justify-center border border-white/20 text-slate-300">
              <XCircle className="h-7 w-7" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-white">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              {state === "timeout"
                ? "Your payment is taking a little longer. Check your email for confirmation."
                : "We couldn't confirm this payment. No charge was made if it failed."}
            </p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-emerald-400 hover:underline">
              <ArrowLeft className="h-4 w-4" /> Back to marketplace
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="grain flex min-h-screen items-center justify-center bg-slate-900 p-6">
      <div className="w-full max-w-md border border-white/10 bg-slate-950 p-10 text-center" data-testid="payment-cancel">
        <div className="mx-auto flex h-14 w-14 items-center justify-center border border-white/20 text-slate-300">
          <XCircle className="h-7 w-7" />
        </div>
        <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-white">Checkout cancelled</h1>
        <p className="mt-2 text-sm text-slate-400">No worries — your cart is safe and no charge was made.</p>
        <button onClick={() => navigate("/")} className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-emerald-500 px-6 py-3.5 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400">
          Return to marketplace
        </button>
      </div>
    </div>
  );
}
